import { useState, useEffect } from "react";
import { MdWarning } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../hooks/useAlert";

const AlertTablePerformance = ({ vmIds , vmNames }) => {
  const navigate = useNavigate();
  const { getAlertsByVmId, loading, error } = useAlert();
  const [alertsByVm, setAlertsByVm] = useState({});
  const [loadingStates, setLoadingStates] = useState({});

  useEffect(() => {
    if (vmIds && vmIds.length > 0) {
      fetchAlertsForAllVms();
    }
  }, [vmIds]);

  const fetchAlertsForAllVms = async () => {
    for (const vmId of vmIds) {
      setLoadingStates(prev => ({ ...prev, [vmId]: true }));
      
      const result = await getAlertsByVmId(vmId, 0);
      
      if (result.success && result.data) {
        setAlertsByVm(prev => ({
          ...prev,
          [vmId]: {
            alerts: result.data.alerts || [],
            totalCount: result.data.totalCount || 0,
            vmName: result.data.vmName || `VM ${vmId}`
          }
        }));
      }
      
      setLoadingStates(prev => ({ ...prev, [vmId]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const numSeconds = parseInt(seconds);
    if (numSeconds < 60) {
      return numSeconds + "s";
    } else if (numSeconds < 3600) {
      const minutes = Math.floor(numSeconds / 60);
      const remainingSeconds = numSeconds % 60;
      if (remainingSeconds === 0) {
        return minutes + "m";
      }
      return minutes + "m " + remainingSeconds + "s";
    } else {
      const hours = Math.floor(numSeconds / 3600);
      const remainingMinutes = Math.floor((numSeconds % 3600) / 60);
      if (remainingMinutes === 0) {
        return hours + "h";
      }
      return hours + "h " + remainingMinutes + "m";
    }
  };

  const formatMetricValue = (alertName, metricValue) => {
    if (metricValue === undefined || metricValue === null) return "0";
    
    const nameLower = alertName.toLowerCase();
    
    if (nameLower.includes("cpu")) {
      return `${metricValue}%`;
    } else if (nameLower.includes("ram") || nameLower.includes("memory")) {
      return `${metricValue} MB`;
    }
    
    return metricValue;
  };

  const getStatusBadge = (condition) => {
    if (condition === "Fired") {
      return (
        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium border bg-red-500/20 text-red-400 border-red-500">
          Fired
        </span>
      );
    }
    return (
      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium border bg-green-500/20 text-green-400 border-green-500">
        Resolved
      </span>
    );
  };

  const handleSeeAllAlerts = (vmId, vmName) => {
    navigate('/dashboard/alert', { state: { selectedVmId: vmId, selectedVmName: vmName } });
  };

  if (!vmIds || vmIds.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">No VM selected</p>
        </div>
      </div>
    );
  }

  if (loading && Object.keys(alertsByVm).length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3 text-sm sm:text-base">Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {vmIds.map((vmId) => {
        const vmData = alertsByVm[vmId];
        const isLoading = loadingStates[vmId];
        
        if (isLoading) {
          return (
            <div key={vmId} className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
              <div className="text-center py-6 sm:py-8">
                <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-400 mt-3 text-sm sm:text-base">Loading alerts for VM {vmId}...</p>
              </div>
            </div>
          );
        }
        
        if (!vmData || vmData.alerts.length === 0) {
          return (
            <div key={vmId} className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
                <div className="flex flex-wrap items-center gap-2">
                  <MdWarning className="text-primary-cyan text-xl sm:text-2xl" />
                  <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts</h3>
                  <span className="text-xs sm:text-sm text-gray-400 ml-0 sm:ml-2">No Alerts</span>
                </div>
                <button
                  disabled
                  className="bg-gray-600 cursor-not-allowed text-white px-2 sm:px-3 py-1 rounded text-xs sm:text-sm transition opacity-50"
                >
                  See All Alerts
                </button>
              </div>
              <div className="text-center py-6 sm:py-8">
                <p className="text-gray-400 text-sm sm:text-base">No alerts found for this VM</p>
              </div>
            </div>
          );
        }
        
        return (
          <div key={vmId} className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
              <div className="flex flex-wrap items-center gap-2">
                <MdWarning className="text-primary-cyan text-xl sm:text-2xl" />
                <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts</h3>
                <span className="text-xs sm:text-sm text-gray-400 ml-0 sm:ml-2 break-words">
                  ({vmData.vmName} - Total: {vmData.totalCount})
                </span>
              </div>
              <button
                onClick={() => handleSeeAllAlerts(vmId, vmData.vmName)}
                className="bg-primary-cyan hover:bg-primary-cyan/80 text-white px-2 sm:px-3 py-1 rounded text-xs sm:text-sm transition"
              >
                See All Alerts
              </button>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <div className="min-w-[800px] sm:min-w-0">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Alert Name</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Description</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Occurred At</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Metric Value</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Resolved At</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Duration</th>
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-3 text-gray-400 font-medium text-[10px] sm:text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vmData.alerts.map((alert, idx) => (
                      <tr 
                        key={idx} 
                        className="border-b border-gray-700/50 hover:bg-white/5 transition cursor-pointer"
                      >
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          <span className="text-white font-medium text-[10px] sm:text-sm break-words">
                            {alert.alertName}
                          </span>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          <span className="text-gray-300 text-[10px] sm:text-xs break-words">
                            {alert.description || "N/A"}
                          </span>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          <span className="text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
                            {formatDate(alert.occurredAt)}
                          </span>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          <span className="text-gray-300 text-[10px] sm:text-xs">
                            {formatMetricValue(alert.alertName, alert.metricValue)}
                          </span>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          <span className="text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
                            {alert.resolvedAt ? formatDate(alert.resolvedAt) : "-"}
                          </span>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          <span className="text-gray-300 text-[10px] sm:text-xs">
                            {alert.duration ? formatDuration(alert.duration) : "0s"}
                          </span>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-3">
                          {getStatusBadge(alert.monitorCondition)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AlertTablePerformance;